import React, { useState, useEffect } from 'react';
import {
  FileText, Send, DollarSign, CheckCircle, Clock, AlertTriangle,
  Plus, X, Edit2, Trash2, Eye, Package
} from 'lucide-react';
import {
  safeAsync, Badge, KPI, neuShadow, neuShadowSm, neuShadowInset,
  inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg, overlayStyle,
  modalStyle, modalHeader, modalTitle, closeBtn
} from './crmShared';
import CatalogPicker from './CatalogPicker';
import LineItemOverrideModal from './LineItemOverrideModal';

export default function InvoicesTab({ contacts, allUsers, canCreate, canUpdate, onContactClick }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    doc_type: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editInvoice, setEditInvoice] = useState(null);
  const [kpis, setKpis] = useState({
    outstanding: 0,
    paidLast30d: 0,
    overdueCount: 0,
    avgTimeToPay: 0
  });

  // Load invoices
  useEffect(() => {
    loadInvoices();
  }, [filters]);

  const loadInvoices = async () => {
    setLoading(true);
    /*
     * ⚠ crm:invoices:list RETURNS A BARE ARRAY (routes/crm.js:11758), not
     * { success, data }. This read used to go to the phantom crm:entity:action
     * channel and the shape never mattered because nothing ever came back —
     * re-pointing it without adapting here would have swapped a silent no-op
     * for a silently empty table, which is harder to notice, not easier.
     */
    /*
     * ⚠⚠ safeAsync(fn) RETURNS A FUNCTION — it has to be CALLED. `await
     * safeAsync(fn)` awaits the wrapper itself, which is not a promise, so the
     * body NEVER RUNS and the API call is never made. Card P71 found three
     * layers stacked here: the wrapper was never invoked, the channel it would
     * have called does not exist, and an error body would not have surfaced.
     * The trailing () is the first layer.
     */
    const rows = await safeAsync(() => window.zignagenixAPI.getCRMInvoices(filters))();
    if (Array.isArray(rows)) {
      setInvoices(rows);
      calculateKPIs(rows);
    }
    setLoading(false);
  };

  const calculateKPIs = (data) => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const outstanding = data
      .filter(i => i.status === 'sent' || i.status === 'viewed')
      .reduce((sum, i) => sum + (i.total - (i.amountPaid || 0)), 0);

    const paidLast30d = data
      .filter(i => i.status === 'paid' && new Date(i.paidDate) >= thirtyDaysAgo)
      .reduce((sum, i) => sum + i.total, 0);

    const overdueCount = data.filter(i => {
      const dueDate = new Date(i.dueDate);
      return (i.status === 'sent' || i.status === 'viewed') && dueDate < now;
    }).length;

    const paidInvoices = data.filter(i => i.status === 'paid' && i.daysToPayment);
    const avgTimeToPay = paidInvoices.length > 0
      ? Math.round(paidInvoices.reduce((sum, i) => sum + i.daysToPayment, 0) / paidInvoices.length)
      : 0;

    setKpis({ outstanding, paidLast30d, overdueCount, avgTimeToPay });
  };

  /*
   * ⛔ THIS MARKS. IT DOES NOT SEND. crm:invoices:send (routes/crm.js:11978)
   * runs one UPDATE — status='sent', sent_at=NOW() — and writes an audit row.
   * Nothing is emailed and no Stripe invoice is created. The button text and
   * the banner above the table both say so; card P71 deliberately did NOT wire
   * Stripe from here. The real path is Operations > Revenue > Invoices.
   */
  const handleMarkSent = async (id) => {
    const result = await safeAsync(() => window.zignagenixAPI.sendCRMInvoice(id))();
    // safeAsync returns undefined on an { error: true } body as well as on a
    // throw, and has already shown the error, so this is the success branch.
    if (result) loadInvoices();
  };

  /*
   * ⛔⛔ THE AMOUNT PROMPT IS GONE, AND REMOVING IT IS THE HONEST CHANGE.
   *
   * crm:invoices:mark-paid takes ONE argument (routes/crm.js, signature
   * `(event, session, id)`) and runs `UPDATE crm_invoices SET status='paid',
   * paid_at=NOW()`. It records no amount and no payment method. The old
   * prompt asked a staff member to type an amount, parsed it, sent it — and
   * the server dropped it on the floor.
   *
   * That is worse than a silent button: a partial payment typed here would
   * have marked the whole invoice paid while appearing to record the figure.
   * Asking for a number nobody stores is a lie with a keyboard in front of it.
   * The confirm below says exactly what will happen instead.
   *
   * ⇒ Recording an amount needs a schema and a handler that accept one. That
   * is a separate card, not something to infer from a prompt that already
   * existed.
   */
  const handleMarkPaid = async (id) => {
    if (!window.confirm(
      'Mark this invoice PAID in full?\n\n'
      + 'This records the status only — no amount and no payment method are stored, '
      + 'and nothing is charged. Payments taken through Stripe live in '
      + 'Operations > Revenue > Invoices.',
    )) return;
    const result = await safeAsync(() => window.zignagenixAPI.markCRMInvoicePaid(id))();
    if (result) loadInvoices();
  };

  // ⛔ UNREACHABLE BY DESIGN — the control that called this is disabled in the
  // table below. Kept so the requirement stays visible for the soft-delete
  // card; deleting it would hide what the button was for.
  // eslint-disable-next-line no-unused-vars
  const handleDelete = async (id) => {
    if (window.confirm('Delete this invoice?')) {
      const result = await safeAsync(() => window.zignagenixAPI.deleteCRMInvoice(id))();
      if (result) loadInvoices();
    }
  };

  const handleSave = async (data) => {
    // ⚠ The trailing () on both arms — see the note in loadInvoices.
    const result = editInvoice
      ? await safeAsync(() => window.zignagenixAPI.updateCRMInvoice(editInvoice.id, data))()
      : await safeAsync(() => window.zignagenixAPI.createCRMInvoice(data))();

    if (result) {
      loadInvoices();
      setShowCreateModal(false);
      setEditInvoice(null);
    }
  };

  const statusBadgeColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      viewed: 'bg-cyan-100 text-cyan-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-200 text-gray-600'
    };
    return colors[status] || colors.draft;
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filters.search && !inv.number.includes(filters.search) && !inv.contact?.name?.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.status && inv.status !== filters.status) return false;
    if (filters.doc_type && inv.doc_type !== filters.doc_type) return false;
    return true;
  });

  return (
    <div className="p-6 space-y-6" style={{ background: '#f5f5f7' }}>
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Outstanding" value={`$${kpis.outstanding.toLocaleString()}`} icon={FileText} />
        <KPI label="Paid (30d)" value={`$${kpis.paidLast30d.toLocaleString()}`} icon={CheckCircle} />
        <KPI label="Overdue" value={kpis.overdueCount} icon={AlertTriangle} />
        <KPI label="Avg Days to Pay" value={`${kpis.avgTimeToPay}d`} icon={Clock} />
      </div>

      {/* Filters & Actions */}
      <div style={cardWrap} className="space-y-4">
        {/*
          Card P71. The banner states the boundary once, at the top, where a
          staff member forming an intention will see it — not only on the
          button they press afterwards.
        */}
        <div
          data-testid="invoices-not-emailed-banner"
          style={{
            background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E',
            borderRadius: 6, padding: '8px 10px', fontSize: 12, marginBottom: 12,
          }}
        >
          Nothing is emailed from this tab. <strong>Mark as sent</strong> records the
          status in the CRM only. To email an invoice to a patient, use{' '}
          <strong>Operations &rarr; Revenue &rarr; Invoices</strong>.
        </div>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Invoices & Estimates</h3>
          {canCreate && (
            <button style={btnPrimary} onClick={() => { setEditInvoice(null); setShowCreateModal(true); }}>
              <Plus size={16} /> New Invoice
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input
            type="text"
            placeholder="Search..."
            style={inp}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <select style={inp} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="viewed">Viewed</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
          <select style={inp} value={filters.doc_type} onChange={(e) => setFilters({ ...filters, doc_type: e.target.value })}>
            <option value="">All Types</option>
            <option value="invoice">Invoice</option>
            <option value="estimate">Estimate</option>
            <option value="quote">Quote</option>
          </select>
          <input
            type="date"
            style={inp}
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          />
          <input
            type="date"
            style={inp}
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
          />
        </div>
      </div>

      {/* Invoice List */}
      <div className="responsive-scroll" style={{ ...cardWrap, overflowX: 'auto' }}>
        {loading ? (
          <p style={emptyMsg}>Loading...</p>
        ) : filteredInvoices.length === 0 ? (
          <p style={emptyMsg}>No invoices found</p>
        ) : (
          <table className="w-full text-sm" style={{ minWidth: 720 }}>
            <thead className="border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold">Number</th>
                <th className="text-left py-3 px-4 font-semibold">Contact</th>
                <th className="text-right py-3 px-4 font-semibold">Total</th>
                <th className="text-right py-3 px-4 font-semibold">Due</th>
                <th className="text-left py-3 px-4 font-semibold">Status</th>
                <th className="text-left py-3 px-4 font-semibold">Due Date</th>
                <th className="text-right py-3 px-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((inv) => (
                <tr key={inv.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">{inv.number}</td>
                  <td className="py-3 px-4">
                    {inv.contact?.id ? (
                      <span onClick={() => onContactClick && onContactClick(inv.contact.id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>
                        {inv.contact?.name || 'N/A'}
                      </span>
                    ) : (
                      inv.contact?.name || 'N/A'
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">${inv.total?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-red-600 font-medium">
                    ${((inv.total || 0) - (inv.amountPaid || 0)).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-3 py-1 rounded text-xs font-medium ${statusBadgeColor(inv.status)}`}>
                      {/*
                        ⛔ 'sent' in this table means a staff member pressed
                        "Mark as sent". No email left the system. Spelling that
                        out in the cell matters more than in the banner: the
                        banner is read once, this is read every time.
                      */}
                      {inv.status === 'sent' ? 'Marked sent (not emailed)' : inv.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm">
                    {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '-'}
                  </td>
                  <td className="py-3 px-4 text-right space-x-2">
                    <button style={btnSm} onClick={() => { setSelectedInvoice(inv); setShowDetailModal(true); }} title="View">
                      <Eye size={14} />
                    </button>
                    {inv.status === 'draft' && canUpdate && (
                      <button style={btnSm} onClick={() => { setEditInvoice(inv); setShowCreateModal(true); }} title="Edit">
                        <Edit2 size={14} />
                      </button>
                    )}
                    {(inv.status === 'draft' || inv.status === 'sent') && canUpdate && (
                      <button
                        style={btnSm}
                        onClick={() => handleMarkSent(inv.id)}
                        title="Mark as sent — records it in the CRM. Nothing is emailed."
                      >
                        <Send size={14} /> Mark as sent
                      </button>
                    )}
                    {(inv.status === 'sent' || inv.status === 'viewed') && canUpdate && (
                      <button style={btnSm} onClick={() => handleMarkPaid(inv.id)} title="Mark Paid">
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {/*
                      ⛔⛔ DISABLED — A HARD DELETE FROM A BUTTON (review round 2).
                      crm:invoices:delete runs TWO unconditional DELETEs — the
                      line items, then the invoice (routes/crm.js) — with no
                      archive, no soft flag and nothing to restore from. The
                      standing rule is that deletes are manual and
                      human-reviewed, and "inward-only" was the only test this
                      control passed when it was wired in round 1. Being
                      inward-only is necessary and plainly not sufficient.
                      ⚠ Soft-delete is NOT added here: that is a schema change
                      and a decision, not something to infer from a button that
                      was dead until yesterday.
                    */}
                    {canUpdate && (
                      <button
                        style={{ ...btnDanger, opacity: 0.5, cursor: 'not-allowed' }}
                        onClick={undefined}
                        disabled
                        title="Not available yet — deleting an invoice is permanent and is not done from this screen."
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CreateInvoiceModal
          contacts={contacts}
          invoice={editInvoice}
          onSave={handleSave}
          onClose={() => { setShowCreateModal(false); setEditInvoice(null); }}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <InvoiceDetailModal invoice={selectedInvoice} onClose={() => setShowDetailModal(false)} onContactClick={onContactClick} />
      )}
    </div>
  );
}

function CreateInvoiceModal({ contacts, invoice, onSave, onClose }) {
  const [formData, setFormData] = useState(
    invoice || {
      contact_id: '',
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      terms: 'Net 30',
      notes: '',
      doc_type: 'invoice',
      items: []
    }
  );
  const [items, setItems] = useState(invoice?.items || []);

  // Catalog flow state — opens CatalogPicker → on select opens
  // LineItemOverrideModal → on confirm appends a catalog-aware line item
  // alongside any existing free-text lines. Source of truth for the line
  // item shape stays compatible with the legacy { description, quantity,
  // unitPrice } items so save logic doesn't need to know which path
  // produced each line.
  const [showCatalogPicker, setShowCatalogPicker] = useState(false);
  const [overrideItem, setOverrideItem] = useState(null);

  const handleCatalogSelect = (catalogRow) => {
    setShowCatalogPicker(false);
    setOverrideItem(catalogRow);
  };

  const handleOverrideConfirm = (line) => {
    setOverrideItem(null);
    setItems([
      ...items,
      {
        // Legacy / display fields
        description: line.displayName,
        quantity: line.quantity,
        unitPrice: line.unitPriceCents / 100,
        taxable: true,
        // Catalog metadata — picked up by the backend's catalog-aware
        // billing:invoices:create handler.
        catalogSku: line.catalogSku,
        listPriceCents: line.listPriceCents,
        unitPriceCents: line.unitPriceCents,
        overrideAmountCents: line.overrideAmountCents,
        overrideReason: line.overrideReason,
      },
    ]);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unitPrice: 0, taxable: true }]);
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...items];
    newItems[idx][field] = value;
    setItems(newItems);
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    const tax = items.reduce((sum, item) => item.taxable ? sum + (item.quantity * item.unitPrice * 0.1) : sum, 0);
    return { subtotal, tax, total: subtotal + tax };
  };

  const { subtotal, tax, total } = calculateTotals();

  const handleSubmit = () => {
    onSave({ ...formData, items, total, tax, subtotal });
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <div style={modalHeader}>
          <h2 style={modalTitle}>{invoice ? 'Edit Invoice' : 'New Invoice'}</h2>
          <button style={closeBtn} onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Contact</label>
              <select style={inp} value={formData.contact_id} onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}>
                <option value="">Select contact...</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Document Type</label>
              <select style={inp} value={formData.doc_type} onChange={(e) => setFormData({ ...formData, doc_type: e.target.value })}>
                <option value="invoice">Invoice</option>
                <option value="estimate">Estimate</option>
                <option value="quote">Quote</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Issue Date</label>
              <input type="date" style={inp} value={formData.issueDate} onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Due Date</label>
              <input type="date" style={inp} value={formData.dueDate} onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })} />
            </div>
          </div>

          {/* Line Items */}
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">Line Items</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={{ ...btnSm, background: '#2E8B98', color: '#fff', border: 'none' }}
                  onClick={() => setShowCatalogPicker(true)}
                  type="button"
                >
                  <Package size={14} /> Add from catalog
                </button>
                <button style={btnSm} onClick={addItem} type="button">
                  <Plus size={14} /> Add custom item
                </button>
              </div>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {items.map((item, idx) => (
                <div key={idx} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 70px 110px 80px 36px',
                  gap: 8, alignItems: 'center',
                  padding: '6px 8px',
                  background: item.catalogSku ? 'rgba(46,139,152,0.06)' : '#FFFFFF',
                  border: item.catalogSku ? '1px solid rgba(46,139,152,0.18)' : '1px solid #DEE2E6',
                  borderRadius: 8,
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    {item.catalogSku ? (
                      <>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#212529', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.description}
                        </span>
                        <span style={{ fontSize: 10, color: '#6C757D' }}>
                          <span style={{ fontFamily: 'ui-monospace, monospace' }}>{item.catalogSku}</span>
                          {item.overrideAmountCents !== 0 && item.listPriceCents != null && (
                            <>
                              {" · was "}
                              <span style={{ textDecoration: 'line-through' }}>
                                ${(item.listPriceCents / 100).toFixed(2)}
                              </span>
                              {" · "}
                              <span style={{ color: item.overrideAmountCents < 0 ? '#0F766E' : '#B45309', fontWeight: 600 }}>
                                {item.overrideAmountCents < 0 ? '−' : '+'}${Math.abs(item.overrideAmountCents / 100).toFixed(2)}
                              </span>
                              {item.overrideReason && <span> · {String(item.overrideReason).slice(0, 30)}</span>}
                            </>
                          )}
                        </span>
                      </>
                    ) : (
                      <input
                        type="text"
                        placeholder="Description"
                        style={inp}
                        value={item.description}
                        onChange={(e) => updateItem(idx, 'description', e.target.value)}
                      />
                    )}
                  </div>
                  <input
                    type="number"
                    placeholder="Qty"
                    style={inp}
                    value={item.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value))}
                  />
                  <input
                    type="number"
                    placeholder="Unit Price"
                    style={inp}
                    value={item.unitPrice}
                    onChange={(e) => updateItem(idx, 'unitPrice', parseFloat(e.target.value))}
                    disabled={!!item.catalogSku}
                    title={item.catalogSku ? 'Catalog price set via override modal; edit by removing this line and re-adding.' : ''}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    ${(item.quantity * item.unitPrice).toFixed(2)}
                  </span>
                  <button style={btnDanger} onClick={() => removeItem(idx)} type="button">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {items.length === 0 && (
                <p style={{ fontSize: 12, color: '#6C757D', padding: '12px 0', textAlign: 'center' }}>
                  No line items yet. Add from the master catalog or create a custom line.
                </p>
              )}
            </div>
          </div>

          {/* Catalog flow modals */}
          {showCatalogPicker && (
            <CatalogPicker
              onSelect={handleCatalogSelect}
              onCancel={() => setShowCatalogPicker(false)}
            />
          )}
          {overrideItem && (
            <LineItemOverrideModal
              item={overrideItem}
              onConfirm={handleOverrideConfirm}
              onCancel={() => setOverrideItem(null)}
            />
          )}

          {/* Totals */}
          <div className="bg-gray-50 p-4 rounded space-y-2">
            <div className="flex justify-between"><span>Subtotal:</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Tax (10%):</span><span>${tax.toFixed(2)}</span></div>
            <div className="flex justify-between font-semibold text-lg"><span>Total:</span><span>${total.toFixed(2)}</span></div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Terms</label>
            <input type="text" style={inp} value={formData.terms} onChange={(e) => setFormData({ ...formData, terms: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea style={inp} rows={3} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t">
          <button style={btnPrimary} onClick={handleSubmit}>Save Invoice</button>
          <button style={{ ...inp, cursor: 'pointer' }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceDetailModal({ invoice, onClose, onContactClick }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} className="max-w-2xl">
        <div style={modalHeader}>
          <h2 style={modalTitle}>{invoice.number}</h2>
          <button style={closeBtn} onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Contact:</strong> {invoice.contact?.id ? (
                <span onClick={() => onContactClick && onContactClick(invoice.contact.id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>
                  {invoice.contact?.name}
                </span>
              ) : (
                invoice.contact?.name
              )}
            </div>
            <div><strong>Type:</strong> {invoice.doc_type}</div>
            <div><strong>Issue Date:</strong> {new Date(invoice.issueDate).toLocaleDateString()}</div>
            <div><strong>Due Date:</strong> {new Date(invoice.dueDate).toLocaleDateString()}</div>
            <div><strong>Status:</strong> <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">{invoice.status}</span></div>
            <div><strong>Terms:</strong> {invoice.terms}</div>
          </div>

          {invoice.items?.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Line Items</h3>
              <div className="responsive-scroll">
                <table className="w-full text-sm" style={{ minWidth: 540 }}>
                  <thead className="border-b"><tr><th className="text-left py-2">Description</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Unit Price</th><th className="text-right py-2">Total</th></tr></thead>
                  <tbody>
                    {invoice.items.map((item, idx) => (
                      <tr key={idx} className="border-b"><td className="py-2">{item.description}</td><td className="text-right">{item.quantity}</td><td className="text-right">${item.unitPrice.toFixed(2)}</td><td className="text-right">${(item.quantity * item.unitPrice).toFixed(2)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal:</span><span>${invoice.subtotal?.toFixed(2) || '0.00'}</span></div>
            <div className="flex justify-between"><span>Tax:</span><span>${invoice.tax?.toFixed(2) || '0.00'}</span></div>
            <div className="flex justify-between font-semibold text-base"><span>Total:</span><span>${invoice.total?.toFixed(2)}</span></div>
            {invoice.amountPaid > 0 && <div className="flex justify-between text-green-700"><span>Paid:</span><span>${invoice.amountPaid?.toFixed(2)}</span></div>}
          </div>

          {invoice.notes && <div className="text-sm"><strong>Notes:</strong> {invoice.notes}</div>}
        </div>

        <div className="p-6 border-t">
          <button style={btnPrimary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
